import { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { MagnifyingGlass, X } from '@phosphor-icons/react';

import { Sidebar } from '@/components/layout/Sidebar/Sidebar';
import { BottomNav } from '@/components/layout/BottomNav/BottomNav';
import { LogoutOverlay } from '@/components/features/LogoutOverlay/LogoutOverlay';
import { useAuthStore } from '@/stores';
import { ROUTES } from '@/constants';

import styles from './AppShell.module.css';

export function AppShell() {
    const navigate = useNavigate();
    const location = useLocation();
    const { isLoggingOut } = useAuthStore();
    const [searchParams] = useSearchParams();
    const isFeed = location.pathname === ROUTES.feed;
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchValue, setSearchValue] = useState(searchParams.get('search') || '');
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleSearchChange = useCallback(
        (value: string) => {
            setSearchValue(value);
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => {
                if (value.trim()) {
                    navigate(`${ROUTES.feed}?search=${encodeURIComponent(value.trim())}`);
                } else {
                    navigate(ROUTES.feed);
                }
            }, 300);
        },
        [navigate]
    );

    const handleClearSearch = () => {
        setSearchValue('');
        navigate(ROUTES.feed);
        setSearchOpen(false);
    };

    const handleSearchToggle = () => {
        setSearchOpen((prev) => !prev);
    };

    useEffect(() => {
        if (searchOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [searchOpen]);

    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, []);

    return (
        <div className={styles.shell}>
            <LogoutOverlay visible={isLoggingOut} />
            <Sidebar />
            <main className={styles.main}>
                {isFeed && (
                    <header className={styles.header}>
                        <div className={styles.searchArea}>
                            <button
                                className={styles.searchToggle}
                                onClick={handleSearchToggle}
                                aria-label="Buscar"
                            >
                                <MagnifyingGlass size={20} />
                            </button>
                            <div className={`${styles.searchInputWrapper} ${searchOpen ? styles.searchOpen : ''}`}>
                                <MagnifyingGlass size={16} className={styles.searchIcon} />
                                <input
                                    ref={inputRef}
                                    className={styles.searchInput}
                                    type="text"
                                    placeholder="Buscar encuestas..."
                                    value={searchValue}
                                    onChange={(e) => handleSearchChange(e.target.value)}
                                    aria-label="Buscar encuestas"
                                />
                                {searchValue && (
                                    <button
                                        className={styles.clearBtn}
                                        onClick={handleClearSearch}
                                        aria-label="Limpiar busqueda"
                                    >
                                        <X size={14} />
                                    </button>
                                )}
                            </div>
                        </div>
                    </header>
                )}
                <div className={styles.content}>
                    <Outlet />
                </div>
            </main>
            <BottomNav />
        </div>
    );
}
